import isEqual from 'lodash/isEqual';

import {
    appendFieldError,
    createField,
    createInputField,
    createTextareaField,
    disableFormControls,
    getDirectFieldError,
    normalizeFieldErrors
} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const defaultEmail = {
    fromName: '',
    toAddresses: [],
    subject: '',
    html: '',
    text: null
};

const emailListFields = [
    'toAddresses',
    'ccAddresses',
    'bccAddresses',
    'replyToAddresses',
    'attachments'
];

const optionalEmailListFields = [
    'ccAddresses',
    'bccAddresses',
    'replyToAddresses',
    'attachments'
];

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : value;

const normalizeStringList = value => Array.isArray(value)
    ? value
        .map(item => typeof item === 'undefined' || item === null ? '' : String(item))
        .filter(item => item.length > 0)
    : value;

const normalizeEmail = email => {
    if (!isPlainObject(email)) {
        return cloneValue(defaultEmail);
    }

    const normalizedEmail = cloneValue(email);

    emailListFields.forEach(fieldName => {
        if (hasOwnProperty(email, fieldName)) {
            normalizedEmail[fieldName] = normalizeStringList(email[fieldName]);
        }
    });

    return normalizedEmail;
};

const hasRealListValues = values => Array.isArray(values) && values.length > 0;

const normalizeEmailPatch = (email, patch) => {
    const normalizedPatch = cloneValue(patch || {});

    optionalEmailListFields.forEach(fieldName => {
        if (!hasOwnProperty(normalizedPatch, fieldName) || hasRealListValues(normalizedPatch[fieldName])) {
            return;
        }

        if (!hasOwnProperty(email, fieldName)) {
            delete normalizedPatch[fieldName];
        } else if (email[fieldName] === null) {
            normalizedPatch[fieldName] = null;
        }
    });

    return normalizedPatch;
};

const getResource = (mxResources, key) => mxResources.get(key);

const fieldValue = (email, fieldName) => normalizeString(email[fieldName]);

const listFieldValue = (email, fieldName) => Array.isArray(email[fieldName]) ? email[fieldName] : [];

const serializeDropdownValues = values => normalizeStringList(values).join(',');

const deserializeDropdownValues = value => normalizeStringList(String(value || '').split(','));

const appendJsonExpressionHelpToContainer = (container, help) => {
    if (!help || container.querySelector('.json-expression-help-field')) {
        return;
    }

    const helpField = document.createElement('div');
    helpField.className = 'field json-expression-help-field';
    helpField.insertAdjacentHTML('beforeend', help);
    container.appendChild(helpField);
};

const createErrorCountLabel = error => {
    const errorCount = normalizeFieldErrors(error).length;
    if (errorCount === 0) {
        return null;
    }

    const errorLabel = document.createElement('span');
    errorLabel.className = 'ui mini red circular label';
    errorLabel.textContent = String(errorCount);
    return errorLabel;
};

const createBodyTabItem = ({id, label, error, active = false}) => {
    const tab = document.createElement('a');
    tab.className = `${active ? 'active ' : ''}item`;
    tab.setAttribute('data-tab', id);
    tab.appendChild(document.createTextNode(label));

    const errorLabel = createErrorCountLabel(error);
    if (errorLabel) {
        tab.appendChild(errorLabel);
    }

    return tab;
};

const createBodyTabSegment = ({id, field, active = false}) => {
    const segment = document.createElement('div');
    segment.className = `ui bottom attached ${active ? 'active ' : ''}tab segment email-body-tab-segment`;
    segment.setAttribute('data-tab', id);
    segment.appendChild(field);
    return segment;
};

const bodyTabFields = {
    emailHtmlBodyTab: 'html',
    emailTextBodyTab: 'text'
};

const mountBodyEditor = (textarea, onChange) => {
    let editorControl = null;
    let destroyed = false;
    let currentValue = textarea.value;
    let pendingRefresh = false;

    const emitChange = value => {
        if (currentValue === value) {
            return;
        }
        currentValue = value;
        onChange(value);
    };

    textarea.addEventListener('change', event => {
        if (!textarea.disabled && !textarea.readOnly) {
            emitChange(event.target.value);
        }
    });

    import(/* webpackChunkName: "text-editor" */ '../../TextEditor')
        .then(module => {
            if (destroyed) {
                return;
            }
            editorControl = module.textEditor(textarea, textarea.value, {onChange: emitChange});
            if (pendingRefresh) {
                pendingRefresh = false;
                editorControl.refresh?.();
            }
        })
        .catch(() => {
        });

    return {
        sync() {
            if (editorControl?.sync) {
                return editorControl.sync();
            }
            if (!textarea.disabled && !textarea.readOnly) {
                emitChange(textarea.value);
            }
            return textarea.value;
        },
        setValue(value) {
            currentValue = value || '';
            textarea.value = currentValue;
            editorControl?.setValue?.(currentValue);
        },
        refresh() {
            if (editorControl?.refresh) {
                editorControl.refresh();
                return;
            }
            pendingRefresh = true;
        },
        destroy() {
            destroyed = true;
            pendingRefresh = false;
            editorControl?.destroy?.();
        }
    };
};

const createEmailTaskForm = ({
                                 task = {},
                                 taskErrors = {},
                                 editable = true,
                                 mxResources,
                                 activeBodyTabId: initialActiveBodyTabId = 'emailHtmlBodyTab',
                                 onEmailChange = () => {}
                             } = {}) => {
    let email = normalizeEmail(task.email);
    let jsonExpressionHelp = null;

    const container = document.createElement('div');
    container.id = 'emailEditor';
    container.className = 'ui field';
    const bodyEditorControls = [];
    let activeBodyTabId = bodyTabFields[initialActiveBodyTabId]
        ? initialActiveBodyTabId
        : 'emailHtmlBodyTab';

    appendFieldError(container, taskErrors?.message || getDirectFieldError(taskErrors?.fields?.email), {className: 'email-error'});

    const fromNameField = createInputField({
        label: getResource(mxResources, 'emailFromName'),
        id: 'emailFromName',
        name: 'email.fromName',
        type: 'text',
        className: 'required',
        value: fieldValue(email, 'fromName'),
        error: taskErrors?.fields?.email?.fromName
    });
    const fromNameInput = fromNameField.querySelector('input');
    fromNameInput.addEventListener('change', event => emitEmail({fromName: event.target.value}));
    container.appendChild(fromNameField);

    const listFieldElements = {};

    const subjectField = createInputField({
        label: getResource(mxResources, 'emailSubject'),
        id: 'emailSubject',
        name: 'email.subject',
        type: 'text',
        className: 'required',
        value: fieldValue(email, 'subject'),
        error: taskErrors?.fields?.email?.subject
    });
    const subjectInput = subjectField.querySelector('input');
    subjectInput.addEventListener('change', event => emitEmail({subject: event.target.value}));
    container.appendChild(subjectField);

    const htmlField = createTextareaField({
        id: 'emailHtml',
        name: 'email.html',
        rows: 8,
        value: fieldValue(email, 'html'),
        error: taskErrors?.fields?.email?.html
    });
    const htmlTextarea = htmlField.querySelector('textarea');
    htmlTextarea.value = fieldValue(email, 'html');
    htmlTextarea.classList.add('html-editor');

    const textField = createTextareaField({
        id: 'emailText',
        name: 'email.text',
        rows: 5,
        value: fieldValue(email, 'text'),
        error: taskErrors?.fields?.email?.text
    });
    const textTextarea = textField.querySelector('textarea');
    textTextarea.value = fieldValue(email, 'text');
    textTextarea.classList.add('plain-text-editor');

    if (!editable) {
        [htmlTextarea, textTextarea].forEach(textarea => {
            textarea.disabled = true;
            textarea.readOnly = true;
            textarea.setAttribute('disabled', '');
            textarea.setAttribute('readonly', '');
        });
    }

    bodyEditorControls.push(
        {
            fieldName: 'html',
            editorControl: mountBodyEditor(htmlTextarea, value => emitEmail({html: value}))
        },
        {
            fieldName: 'text',
            editorControl: mountBodyEditor(textTextarea, value => emitEmail({text: value}))
        }
    );

    const bodyTabsField = document.createElement('div');
    bodyTabsField.className = 'field email-body-tabs-field';

    const bodyTabMenu = document.createElement('div');
    bodyTabMenu.className = 'ui top attached tabular menu email-body-tab-menu';
    bodyTabMenu.appendChild(createBodyTabItem({
        id: 'emailTextBodyTab',
        label: getResource(mxResources, 'emailText'),
        error: taskErrors?.fields?.email?.text,
        active: activeBodyTabId === 'emailTextBodyTab'
    }));
    bodyTabMenu.appendChild(createBodyTabItem({
        id: 'emailHtmlBodyTab',
        label: getResource(mxResources, 'emailHtml'),
        error: taskErrors?.fields?.email?.html,
        active: activeBodyTabId === 'emailHtmlBodyTab'
    }));

    bodyTabsField.appendChild(bodyTabMenu);
    bodyTabsField.appendChild(createBodyTabSegment({
        id: 'emailTextBodyTab',
        field: textField,
        active: activeBodyTabId === 'emailTextBodyTab'
    }));
    bodyTabsField.appendChild(createBodyTabSegment({
        id: 'emailHtmlBodyTab',
        field: htmlField,
        active: activeBodyTabId === 'emailHtmlBodyTab'
    }));
    container.appendChild(bodyTabsField);

    const createListDropdown = ({fieldName, placeholder}) => {
        const dropdown = document.createElement('div');
        dropdown.id = `email${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
        dropdown.className = `ui fluid search selection multiple dropdown addition email-multiple-value-dropdown${editable ? '' : ' disabled'}`;
        dropdown.dataset.fieldName = fieldName;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `email.${fieldName}`;
        input.value = serializeDropdownValues(listFieldValue(email, fieldName));
        input.addEventListener('change', event => {
            const target = event.target;
            if (target.disabled || target.readOnly) {
                return;
            }

            const value = target.value;
            setTimeout(() => {
                if (target.isConnected === false) {
                    return;
                }
                emitEmail({[fieldName]: deserializeDropdownValues(value)});
            });
        });
        dropdown.appendChild(input);

        const defaultText = document.createElement('div');
        defaultText.className = 'default text';
        defaultText.textContent = placeholder;
        dropdown.appendChild(defaultText);

        const menu = document.createElement('div');
        menu.className = 'menu';
        listFieldValue(email, fieldName).forEach(value => {
            const item = document.createElement('div');
            item.className = 'item';
            item.dataset.value = value;
            item.textContent = value;
            menu.appendChild(item);
        });
        dropdown.appendChild(menu);
        return dropdown;
    };

    const createListField = ({fieldName, label, placeholder, className = ''}) => {
        const field = createField({
            label,
            className,
            error: taskErrors?.fields?.email?.[fieldName],
            fieldElement: createListDropdown({fieldName, placeholder})
        });
        return field;
    };

    const renderListFields = () => {
        const nextListFieldElements = {
            toAddresses: createListField({
                fieldName: 'toAddresses',
                label: getResource(mxResources, 'emailToAddresses'),
                placeholder: getResource(mxResources, 'emailAddToAddress'),
                className: 'required'
            }),
            ccAddresses: createListField({
                fieldName: 'ccAddresses',
                label: getResource(mxResources, 'emailCcAddresses'),
                placeholder: getResource(mxResources, 'emailAddCcAddress')
            }),
            bccAddresses: createListField({
                fieldName: 'bccAddresses',
                label: getResource(mxResources, 'emailBccAddresses'),
                placeholder: getResource(mxResources, 'emailAddBccAddress')
            }),
            replyToAddresses: createListField({
                fieldName: 'replyToAddresses',
                label: getResource(mxResources, 'emailReplyToAddresses'),
                placeholder: getResource(mxResources, 'emailAddReplyToAddress')
            }),
            attachments: createListField({
                fieldName: 'attachments',
                label: getResource(mxResources, 'emailAttachments'),
                placeholder: getResource(mxResources, 'emailAddAttachment')
            })
        };

        emailListFields.forEach(fieldName => {
            if (listFieldElements[fieldName]) {
                listFieldElements[fieldName].replaceWith(nextListFieldElements[fieldName]);
            }
            listFieldElements[fieldName] = nextListFieldElements[fieldName];
        });
    };

    const syncStructuredFields = () => {
        fromNameInput.value = fieldValue(email, 'fromName');
        subjectInput.value = fieldValue(email, 'subject');
        bodyEditorControls.forEach(({fieldName, editorControl}) =>
            editorControl.setValue?.(fieldValue(email, fieldName)));
        renderListFields();
    };

    const destroyBodyEditors = () => {
        syncBodyEditors();
        bodyEditorControls.forEach(({editorControl}) => editorControl.destroy());
        bodyEditorControls.length = 0;
    };

    const syncBodyEditors = () => {
        bodyEditorControls.forEach(({editorControl}) => editorControl.sync?.());
    };

    const refreshBodyEditor = fieldName => {
        bodyEditorControls
            .find(bodyEditorControl => bodyEditorControl.fieldName === fieldName)
            ?.editorControl
            ?.refresh?.();
    };

    const activeBodyFieldName = () => bodyTabFields[activeBodyTabId] || 'html';

    function emitEmail(patch) {
        const normalizedPatch = normalizeEmailPatch(email, patch);
        const nextEmail = normalizeEmail({
            ...email,
            ...normalizedPatch
        });

        if (isEqual(email, nextEmail)) {
            return;
        }

        email = nextEmail;
        task.email = cloneValue(email);
        onEmailChange(cloneValue(email));
    }

    renderListFields();
    container.insertBefore(listFieldElements.toAddresses, subjectField);
    container.insertBefore(listFieldElements.ccAddresses, subjectField);
    container.insertBefore(listFieldElements.bccAddresses, subjectField);
    container.insertBefore(listFieldElements.replyToAddresses, subjectField);
    container.appendChild(listFieldElements.attachments);

    appendJsonExpressionHelpToContainer(container, jsonExpressionHelp);

    if (!editable) {
        disableFormControls(container);
    }

    return {
        element: container,
        getEmail: () => {
            syncBodyEditors();
            return cloneValue(email);
        },
        setEmail: value => {
            email = normalizeEmail(value);
            task.email = cloneValue(email);
            syncStructuredFields();
        },
        setActiveBodyTab: tabId => {
            if (bodyTabFields[tabId]) {
                activeBodyTabId = tabId;
            }
        },
        refreshActiveBodyEditor: () => refreshBodyEditor(activeBodyFieldName()),
        appendJsonExpressionHelp: help => {
            jsonExpressionHelp = help;
            appendJsonExpressionHelpToContainer(container, jsonExpressionHelp);
        },
        destroy: destroyBodyEditors
    };
};

export {
    createEmailTaskForm,
    defaultEmail,
    normalizeEmail
};
